import React, { useState, useEffect } from "react";
import { ChevronLeft } from "lucide-react";
import Navbar from "../../../sub-comp/Navbar";
import FullScreenImage from "../../../Home/ShowcaseSection";
import Footer from "../../../sub-comp/MainFooter";
import introSec from "../../../../assets/intro-sec.jpg";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import classrom from "../../../../assets/classrom.jpeg";
import global from "../../../../assets/global.jpeg";
import cyberDiff from "../../../../assets/cyber-difference.jpeg";

const CybersecurityIntro = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div>
      <Navbar />
      <div className="min-h-screen bg-white text-black overflow-hidden relative">
        {/* Hero Section */}
        <section
          className={`relative h-[70vh] flex items-center justify-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          {/* Hero Image */}
          <img
            src={introSec}
            alt="Cybersecurity Hero"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative z-10 text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              Introduction to Cybersecurity
            </h1>
            <p className="max-w-2xl mx-auto text-lg">
              Protecting the digital world, one system at a time.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20">
          {/* Breadcrumb */}
          <div className="bg-white">
            <div className="max-w-7xl mx-auto px-4 py-4">
              <Link
                to="/course/cyber-security"
                className="flex items-center text-custom-pink hover:text-custom-pink/70 font-medium transition-all duration-300 hover:translate-x-1 group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
                Back to IT Programs
              </Link>
            </div>
          </div>

          {/* Introduction Paragraphs */}
          <div
            className={`mb-16 transition-all duration-1000 delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
          >
            <p className="text-lg leading-relaxed mb-6 text-black">
              Cybersecurity has emerged as one of the most critical disciplines
              in our digital age, protecting organizations and individuals from
              an ever-evolving landscape of cyber threats. As our world becomes
              increasingly interconnected through digital technologies, the need
              for skilled cybersecurity professionals continues to grow
              exponentially. This comprehensive introduction will guide you
              through the fundamental concepts, principles, and practices that
              form the backbone of modern cybersecurity. You'll discover how
              cybersecurity professionals defend against malicious attacks,
              protect sensitive data, and maintain the integrity of digital
              systems. The field encompasses everything from network security
              and cryptography to incident response and digital forensics,
              offering diverse career paths for those passionate about
              protecting our digital future.
            </p>
            <p className="text-lg leading-relaxed text-black">
              At JR Institute of Information Technology, we believe that
              cybersecurity education should be both theoretical and practical,
              preparing students for real-world challenges. Our program
              emphasizes hands-on learning through simulated cyber attack
              scenarios, security tool implementation, and ethical hacking
              exercises. You'll learn to think like both a defender and an
              attacker, understanding vulnerabilities from multiple perspectives
              to build comprehensive security strategies.
            </p>
          </div>
        </div>

        {/* In the Classroom Section */}
        <section
          className={`w-full mb-16 transition-all duration-1000 delay-900 ${isVisible ? "opacity-100" : "opacity-0"}`}
        >
          <div className="w-full bg-custom-pink py-12">
            <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
              {/* Left Image */}
              <img
                src={classrom}
                alt="Classroom"
                className="w-full h-full object-cover rounded-2xl"
              />

              {/* Content */}
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  IN THE CLASSROOM
                </h2>
                <p className="text-white leading-relaxed mb-6">
                  Students will immerse themselves in interactive cybersecurity
                  labs where they'll learn to identify and analyze various types
                  of cyber threats in controlled environments. Through hands-on
                  exercises, you'll master the fundamentals of network security,
                  understanding how firewalls, intrusion detection systems, and
                  encryption protocols work together to create robust defense
                  mechanisms.
                </p>
                <p className="text-white leading-relaxed">
                  Advanced modules include digital forensics techniques where
                  students learn to investigate cyber incidents and preserve
                  digital evidence for legal proceedings. Ethical hacking
                  sessions teach responsible vulnerability assessment, helping
                  students understand how to identify security weaknesses before
                  malicious actors can exploit them.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Beyond the Classroom Section */}
        <section
          className={`w-full mb-16 transition-all duration-1000 delay-1100 ${isVisible ? "opacity-100" : "opacity-0"}`}
        >
          <div className="w-full relative py-12 bg-white">
            <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
              {/* Content */}
              <div>
                <h2 className="text-4xl font-bold text-custom-pink mb-6">
                  BEYOND THE CLASSROOM
                </h2>
                <p className="text-black leading-relaxed mb-6">
                  The cybersecurity field extends far beyond traditional
                  classroom learning, offering students numerous opportunities
                  to engage with real-world security challenges through
                  internships, industry partnerships, and professional
                  certifications.
                </p>
                <p className="text-black leading-relaxed">
                  Alumni of our cybersecurity program have gone on to work for
                  major corporations, government cybersecurity agencies, and
                  specialized security consulting firms across Tanzania and
                  internationally.
                </p>
              </div>

              {/* Right Image with fade overlay */}
              <div className="relative">
                <img
                  src={global}
                  alt="Global"
                  className="w-full h-full object-cover rounded-2xl"
                />
                <div className="absolute inset-0 bg-white/60 rounded-2xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Cybersecurity + The Difference Section */}
        <section
          className={`w-full transition-all duration-1000 delay-1300 ${isVisible ? "opacity-100" : "opacity-0"}`}
        >
          <div className="w-full py-12 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
              {/* Left Image */}
              <img
                src={cyberDiff}
                alt="Cybersecurity Difference"
                className="w-full h-full object-cover rounded-2xl"
              />

              {/* Content */}
              <div>
                <h2 className="text-4xl font-bold text-custom-pink mb-6">
                  CYBERSECURITY + THE JRIIT DIFFERENCE
                </h2>
                <p className="text-black leading-relaxed mb-6">
                  What sets cybersecurity apart from other IT disciplines is its
                  unique combination of technical expertise, strategic thinking,
                  and constant adaptation to emerging threats in an
                  ever-changing digital landscape.
                </p>
                <p className="text-black leading-relaxed">
                  The cybersecurity profession offers unparalleled job security
                  and growth potential, as the demand for skilled professionals
                  far exceeds the current supply globally.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      <FullScreenImage />
      <Footer />
    </div>
  );
};

export default CybersecurityIntro;
